import { useEffect, useRef, useState } from "react";
import { Send, Building2, Users, Network, Stethoscope } from "lucide-react";
import { assistantChat } from "@/lib/api";
import { ASSISTANT } from "@/constants/testIds";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";

const QUICK_PROMPTS = [
  { icon: Building2, label: "Soy una clínica médico-estética", q: "Tengo una clínica médica con dermatólogo y atendemos todo tipo de pieles. ¿Qué plataforma dermo facial me recomiendas?" },
  { icon: Users, label: "Soy un centro de belleza", q: "Llevo un centro de belleza con 3 cabinas, alto volumen de tratamientos faciales. ¿Qué equipo dermo facial me da mejor ROI?" },
  { icon: Network, label: "Soy una franquicia", q: "Soy responsable de una franquicia con 12 sedes, busco estandarizar tratamientos dermo faciales. ¿Qué propuesta tienes para multi-sede?" },
  { icon: Stethoscope, label: "Soy dermatólogo/a", q: "Soy dermatóloga y quiero tratar cicatrices, flacidez y rejuvenecimiento facial. ¿Cuál es el equipo médico Asirox más completo?" },
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Bienvenido a Asirox. Soy tu asesor virtual especializado en plataformas dermo faciales profesionales. Puedo recomendarte el equipo perfecto según tu modelo de negocio, tipo de piel atendida, volumen y objetivos. ¿Empezamos con una pregunta rápida o cuéntame tu caso?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await assistantChat(sessionId, msg);
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Lo siento, hubo un problema. Inténtalo nuevamente." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-24 relative overflow-hidden">
      <SEO
        title="Asesor IA Dermo Facial · Elige tu Equipo Asirox en Segundos"
        description="Tu asistente virtual con IA Claude Sonnet 4.5 que te recomienda el equipo dermo facial Asirox ideal según tu tipo de centro, volumen y objetivos clínicos. Recomendación personalizada en segundos."
        keywords="asesor IA equipos dermo faciales, recomendador plataforma dermo facial, qué equipo dermo facial comprar, consultor aparatología estética, IA recomendación equipo facial, Claude Sonnet 4.5 dermo facial"
      />
      <PageHero
        overline="Tú Asistente · Claude Sonnet 4.5"
        title="Te asesoramos según"
        highlight="tus necesidades."
        subtitle="Tu asesor virtual experto en plataformas dermo faciales y aparatología estética Asirox. Recomendación personalizada según tu modelo de negocio, volumen y tipologías cutáneas atendidas."
        align="center"
      />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex justify-center -mt-4 mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#ff9c00]/50 ai-bubble">
            <span className="w-2 h-2 rounded-full bg-[#ff9c00] animate-pulse"/>
            <span className="overline">Online · Respuesta en segundos</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {QUICK_PROMPTS.map((p) => (
            <button
              key={p.label}
              onClick={() => send(p.q)}
              disabled={loading}
              className="chat-bubble border border-black/15 bg-[#f7ebd9] p-4 text-left rounded-sm"
            >
              <p.icon size={18} className="text-[#3D1F0A]" strokeWidth={1.6}/>
              <p className="text-sm text-black mt-3 leading-snug font-semibold">{p.label}</p>
            </button>
          ))}
        </div>

        <div ref={listRef} className="mt-10 border border-black/15 bg-white p-6 md:p-8 space-y-5 max-h-[60vh] overflow-y-auto rounded-sm shadow-[0_10px_40px_-15px_rgba(61,31,10,0.20)]">
          {messages.map((m, i) => (
            <div key={i} data-testid={ASSISTANT.message(i)} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`chat-bubble max-w-[80%] px-5 py-4 leading-relaxed whitespace-pre-wrap text-sm md:text-base rounded-sm ${
                m.role === "user" ? "bg-[#ff9c00] text-black font-semibold" : "ai-bubble text-black"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="ai-bubble px-5 py-4 flex gap-1.5 chat-bubble rounded-sm">
                <span className="w-2 h-2 bg-[#ff9c00] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}/>
                <span className="w-2 h-2 bg-[#ff9c00] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}/>
                <span className="w-2 h-2 bg-[#ff9c00] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}/>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <input
            data-testid={ASSISTANT.page_input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Cuéntale a tu asistente sobre tu centro..."
            className="flex-1 bg-white border border-black/15 px-5 py-4 text-black placeholder:text-black/40 focus:border-[#ff9c00] outline-none transition rounded-sm shadow-[0_4px_12px_-4px_rgba(0,0,0,0.08)]"
          />
          <button
            data-testid={ASSISTANT.page_send}
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="px-6 bg-[#ff9c00] text-black font-bold disabled:opacity-40 hover:bg-[#ffb133] transition shadow-[0_4px_12px_-4px_rgba(255,156,0,0.4)]"
          >
            <Send size={16}/>
          </button>
        </div>
        <p className="text-xs text-black/60 mt-4 text-center font-mono">
          Conversación en tiempo real con IA. Las recomendaciones son orientativas; cierra la decisión con un experto Asirox.
        </p>
      </div>
    </div>
  );
}
